/** @jsx jsx */
import type { MouseEvent, PropsWithChildren } from 'react';
import { css, jsx } from '@compiled/react';
import ChildWorkItemsIcon from '@atlaskit/icon/core/child-work-items';
import ChildIssuesIconOld from '@atlaskit/icon/glyph/child-issues';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

type Props = {
	numTotalChildren: number;
	numCompleteChildren: number;
	onClickThrough: (e: MouseEvent) => void;
};
export const ChildIssuesMetadata = ({
	numTotalChildren,
	numCompleteChildren,
	onClickThrough,
}: Props) => {
	const { formatMessage } = useIntl();

	if (!numTotalChildren) {
		return null;
	}

	const message = formatMessage(
		expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.childIssuesMetadataTooltipIssueTermRefresh
			: messages.childIssuesMetadataTooltip,
		{
			complete: numCompleteChildren,
			total: numTotalChildren,
		},
	);

	return (
		<ChildIssuesMetadataWrapper
			onClick={onClickThrough}
			tabIndex={0}
			aria-label={message}
			data-testid="software-backlog.card-list.card.card-contents.child-issues-metadata"
		>
			<ToolTip
				content={message}
				testId="software-backlog.card-list.card.card-contents.child-issues-metadata.tooltip"
			>
				<ChildWorkItemsIcon
					testId="software-backlog.card-list.card.card-contents.child-issues-metadata.child-issues-icon"
					label=""
					color={token('color.icon.subtle')}
					LEGACY_fallbackIcon={ChildIssuesIconOld}
					LEGACY_primaryColor={token('color.icon.subtle')}
					LEGACY_size="small"
				/>
			</ToolTip>
		</ChildIssuesMetadataWrapper>
	);
};

const wrapperStyle = css({
	zIndex: 1 /* surface the element above the interaction layer so tooltips can be triggered */,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		display: 'flex',
	},
	gridColumn: 'child-meta / span 1',
	gridRow: 'card-detail / span 1',
	'&:focus-within': {
		outlineWidth: token('border.width.outline'),
		outlineStyle: 'solid',
		outlineOffset: token('space.025'),
		outlineColor: token('color.border.focused'),
		borderRadius: token('border.radius.050'),
	},
});

const ChildIssuesMetadataWrapper = ({
	children,
	...props
}: PropsWithChildren<{ onClick: (e: MouseEvent) => void; tabIndex: number }>) => (
	<div css={[wrapperStyle]} role="tooltip" {...props}>
		{children}
	</div>
);
